import { useEffect, useLayoutEffect, useRef } from 'react';
import { authClient } from '@/lib/auth-client';
import { desktopInvoke } from '@/lib/desktopApp';
import { useStore } from '@/store/useStore';
import { CrmProvider, useCrm } from './CrmContext';
import { ScreenPopStack } from './ScreenPop';

/**
 * The desktop app's pop-up window (/desktop-popup): a borderless, always-on-top, see-through window
 * in the corner of the screen. It holds the live connection and shows the same cards as the browser;
 * the app sizes the window to the cards and lets clicks through when there are none.
 */

type PopupStatus = 'signed_out' | 'no_crm' | 'connecting' | 'ready';

function PopupWindow() {
  const { data, pops, liveConnected } = useCrm();
  const stack = useRef<HTMLDivElement>(null);

  const status: PopupStatus = data === null ? 'no_crm' : data === undefined || !liveConnected ? 'connecting' : 'ready';
  const detail = data ? (data.features.screenPop ? (data.prefs.extension ? `Extension ${data.prefs.extension}` : 'Set your extension in CRM → Settings') : 'Pop-ups are not switched on for this account') : '';
  useEffect(() => {
    void desktopInvoke('popup_status', { status, detail });
  }, [status, detail]);

  // Tell the app how tall the cards are; 0 hides the window and lets clicks through.
  useLayoutEffect(() => {
    const element = stack.current;
    if (!element) return;
    let last = -1;
    const report = () => {
      const height = pops.length ? Math.ceil(element.getBoundingClientRect().height) + 24 : 0;
      if (height === last) return;
      last = height;
      void desktopInvoke('popup_resize', { height });
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(element);
    return () => observer.disconnect();
  }, [pops.length]);

  return (
    <div ref={stack} className="p-3">
      <ScreenPopStack inline />
    </div>
  );
}

export function DesktopPopupApp() {
  const session = authClient.useSession();
  const theme = useStore((state) => state.theme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
  }, [theme]);

  // The app reloads this page if it stops checking in.
  useEffect(() => {
    const timer = setInterval(() => void desktopInvoke('popup_status', { status: 'alive', detail: '' }), 30_000);
    return () => clearInterval(timer);
  }, []);

  const signedOut = !session.isPending && !session.data?.user;
  useEffect(() => {
    if (signedOut) {
      void desktopInvoke('popup_resize', { height: 0 });
      void desktopInvoke('popup_status', { status: 'signed_out', detail: 'Open Yeastarify and sign in' });
    }
    if (!signedOut) return;
    // Signing in happens in the app's main window; notice it from here.
    const timer = setInterval(() => void session.refetch(), 10_000);
    return () => clearInterval(timer);
  }, [signedOut, session]);

  if (!session.data?.user) return null;
  return (
    <CrmProvider variant="desktopPopup">
      <PopupWindow />
    </CrmProvider>
  );
}
